import React from 'react';
import TelegramIcon from '@mui/icons-material/Telegram';
import { BoundChannelName, WebhookChannelName } from '../../shared/services/scheduledTasksApi';

// Glifos monocromáticos das plataformas (herdam a cor do texto; o MUI não tem
// ícones de marca para elas).
export const SlackGlyph: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M9.5 3a2 2 0 0 0 0 4h2V5a2 2 0 0 0-2-2zm0 5H4a2 2 0 1 0 0 4h5.5a2 2 0 1 0 0-4zm11 2a2 2 0 1 0-4 0v2h2a2 2 0 0 0 2-2zm-5 0V4a2 2 0 1 0-4 0v6a2 2 0 1 0 4 0zm-1 6.5a2 2 0 0 0 0-4h-2v2a2 2 0 0 0 2 2zm0 .5a2 2 0 1 0 0 4h.01A2 2 0 0 0 14.5 17zM3 14.5a2 2 0 1 0 4 0v-2H5a2 2 0 0 0-2 2zm5 0v5.5a2 2 0 1 0 4 0v-5.5a2 2 0 1 0-4 0z" />
  </svg>
);

export const DiscordGlyph: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.865-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.028C.533 9.046-.32 13.58.099 18.058a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .078-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .079.009c.12.099.245.198.372.292a.077.077 0 0 1-.006.128 12.3 12.3 0 0 1-1.873.891.077.077 0 0 0-.041.107c.36.698.772 1.363 1.225 1.993a.076.076 0 0 0 .084.029 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.029zM8.02 15.331c-1.183 0-2.157-1.086-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.419 0 1.333-.956 2.419-2.157 2.419zm7.975 0c-1.183 0-2.157-1.086-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.419 0 1.333-.946 2.419-2.157 2.419z" />
  </svg>
);

export const TeamsGlyph: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="19" cy="7" r="2.5" />
    <path d="M17.2 10.5H22a1 1 0 0 1 1 1v4.2a3.3 3.3 0 0 1-3.3 3.3h-.2a3.3 3.3 0 0 1-2.3-.9z" />
    <path
      fillRule="evenodd"
      d="M3 6.5A1.5 1.5 0 0 1 4.5 5h10A1.5 1.5 0 0 1 16 6.5v10a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 3 16.5zM6 8.3h7v1.8h-2.6V15H8.6v-4.9H6z"
    />
  </svg>
);

export interface WebhookPlatformInfo {
  name: WebhookChannelName;
  label: string;
  Glyph: React.FC<{ size?: number }>;
  placeholder: string;
  helpUrl: string;
  /** Como criar o webhook, em uma frase (o link vai em helpUrl). */
  helpText: string;
}

export const WEBHOOK_PLATFORMS: WebhookPlatformInfo[] = [
  {
    name: 'slack',
    label: 'Slack',
    Glyph: SlackGlyph,
    placeholder: 'https://hooks.slack.com/services/…',
    helpUrl: 'https://api.slack.com/messaging/webhooks',
    helpText: 'Crie um Incoming Webhook no canal desejado e cole a URL.',
  },
  {
    name: 'discord',
    label: 'Discord',
    Glyph: DiscordGlyph,
    placeholder: 'https://discord.com/api/webhooks/…',
    helpUrl: 'https://support.discord.com/hc/pt-br/articles/228383668',
    helpText: 'Nas configurações do canal, vá em Integrações → Webhooks → Novo webhook e copie a URL.',
  },
  {
    name: 'teams',
    label: 'Microsoft Teams',
    Glyph: TeamsGlyph,
    placeholder: 'https://….logic.azure.com/workflows/…',
    helpUrl: 'https://support.microsoft.com/office/8ae491c7-0394-4861-ba59-055e33f75498',
    helpText: 'No canal, abra Fluxos de trabalho → "Postar em um canal quando uma solicitação de webhook for recebida" e copie a URL gerada.',
  },
];

export const TelegramGlyph: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <TelegramIcon style={{ fontSize: size }} />
);

/** Rótulo e glifo de todo canal com destino vinculado (webhooks + Telegram). */
export const BOUND_BY_NAME: Record<BoundChannelName, { label: string; Glyph: React.FC<{ size?: number }> }> = {
  ...(Object.fromEntries(WEBHOOK_PLATFORMS.map((p) => [p.name, { label: p.label, Glyph: p.Glyph }])) as Record<
    WebhookChannelName,
    { label: string; Glyph: React.FC<{ size?: number }> }
  >),
  telegram: { label: 'Telegram', Glyph: TelegramGlyph },
};

/** "teams:juridico" → { channel: 'teams', alias: 'juridico' }; null se não for canal vinculado. */
export function parseBoundEntry(entry: string): { channel: BoundChannelName; alias: string | null } | null {
  const [base, ...rest] = entry.split(':');
  if (!(base in BOUND_BY_NAME)) return null;
  return { channel: base as BoundChannelName, alias: rest.length ? rest.join(':') : null };
}
